import { HttpParams } from '@angular/common/http';
import { GetEndPointSP } from '../get-stacking-plan-endpoint.helper';
import { FilterDataRequest, ToolbarFilterOption } from './filters-toolbar.model';
import { Observable, map } from 'rxjs';
import { DEFAULT_NA_FILTER_OPTION, DEFAULT_UN_FILTER_OPTION } from './filters-toolbar.const';
import { HttpMethods } from '@cbre/http';
import { Injectable } from '@angular/core';

/**
 * Maps a raw `/stack2/{id}/filters` (or HV-11F include-bundle `filters`) body into
 * the toolbar's option list, adding the NA/unaccounted defaults exactly like the
 * dedicated endpoint's response does. Exported so the include-bundle seed path
 * (`FiltersService.seedFilterData`, HV-11F) applies the same mapping.
 */
export function toFilterOptions(
  raw: ToolbarFilterOption[] | null | undefined,
  options: Pick<FilterDataRequest, 'isRemoveUnaccountedEnabled'>
): ToolbarFilterOption[] {
  const { isRemoveUnaccountedEnabled } = options;
  const defaultFilters = isRemoveUnaccountedEnabled
    ? [DEFAULT_NA_FILTER_OPTION]
    : [DEFAULT_NA_FILTER_OPTION, DEFAULT_UN_FILTER_OPTION];
  const filtersFn = (filters: ToolbarFilterOption[]) =>
    isRemoveUnaccountedEnabled ? [...filters] : [...filters, DEFAULT_UN_FILTER_OPTION];

  // No-access responses (dedicated /filters endpoint and the include bundle alike) are an empty
  // `StackingPlan{}` object, not an array: guard explicitly rather than relying on
  // `({}).length > 0` happening to be falsy.
  return Array.isArray(raw) && raw.length > 0 ? filtersFn(raw) : defaultFilters;
}

@Injectable({ providedIn: 'root' })
export class FiltersHttpService {
  constructor(private httpClient: HttpMethods) {}

  /**
   * Get filter data
   */
  public getFilterData(propertyId: number, options: FilterDataRequest): Observable<ToolbarFilterOption[]> {
    const { view, localeId, isRemoveUnaccountedEnabled, propertyType } = options;
    const uri = `/${GetEndPointSP(propertyId)}/filters`;
    let params = new HttpParams();

    if (view != null) {
      params = params.set('view', view);
    }

    if (localeId != null) {
      params = params.set('localeId', localeId.toString());
    }

    if (isRemoveUnaccountedEnabled) {
      params = params.set('removeUnaccountedFilter', isRemoveUnaccountedEnabled.toString());
    }

    if (propertyType != null) {
      params = params.set('usageTypes', propertyType);
    }

    return this.httpClient
      .httpGet(uri, params)
      .pipe(map(filters => toFilterOptions(filters, { isRemoveUnaccountedEnabled })));
  }
}
